import axios from 'axios';
import { API } from '../../utils/api';
import { DOCUMENT_SERVICE_PROVIDER_CODE, ACCESS_TOKEN } from '../../utils/constants';
import authUtil from '../../utils/AuthUtils';

/**
 * Fetches user privileges from the API for the Document Service Provider
 * 
 * @param {string} company_ref - The reference code for the company
 * @param {string} clientTypeCode - The client type code
 * @returns {Promise<Object>} Promise resolving to the privileges for document service
 * @throws {Error} If there is an error fetching privileges
 */
function getPrivileges(company_ref, clientTypeCode) {
  let AUTH_TOKEN = localStorage.getItem(ACCESS_TOKEN);
  if (!AUTH_TOKEN) {
    authUtil.clearSession();
    window.location.href = `${window.location.origin}/login`;
    return Promise.reject(new Error('Authentication token not found'));
  }

  return axios
    .get(API.USER.getPrivileges + `${company_ref}/client-type/service-provider/privileges`, {
      headers: {
        Authorization: `Bearer ${AUTH_TOKEN}`,
        'X-SERVICE-PROVIDER-CODE': DOCUMENT_SERVICE_PROVIDER_CODE,
        'X-COMPANY-REFERENCE': company_ref,
        'X-CLIENT-TYPE-CODE': clientTypeCode
      },
    })
    .then((res) => {
      const privilegesData = res?.data;
      
      if (!Array.isArray(privilegesData) || privilegesData.length === 0) {
        console.warn('No privileges data found');
        return { services: [], privileges: [] };
      }
      
      // Find the client type that matches the requested clientTypeCode
      const clientTypeInfo = privilegesData.find(item => item.clientTypeCode === clientTypeCode);
      
      if (!clientTypeInfo) {
        console.warn(`Client type with code ${clientTypeCode} not found`);
        return { services: [], privileges: [] };
      }
      
      if (!clientTypeInfo.companyServiceProviderDetails || !Array.isArray(clientTypeInfo.companyServiceProviderDetails)) {
        console.warn('No service provider details found for this client type');
        return { services: [], privileges: [] };
      }
      
      // Find the document service provider
      const documentServiceProvider = clientTypeInfo.companyServiceProviderDetails.find(
        provider => provider.serviceProviderCode === DOCUMENT_SERVICE_PROVIDER_CODE
      );
      
      if (!documentServiceProvider) {
        console.warn(`Service provider with code ${DOCUMENT_SERVICE_PROVIDER_CODE} not found for client type ${clientTypeCode}`);
        return { services: [], privileges: [] };
      }
      
      // Extract service privilege information
      const servicePrivilegeInfo = documentServiceProvider.ServicePrivilegeInfo || [];
      
      // Create a structured object with all privileges
      const documentServicePrivileges = {
        serviceProviderCode: documentServiceProvider.serviceProviderCode,
        serviceProviderName: documentServiceProvider.serviceProviderName,
        companyServiceProviderId: documentServiceProvider.companyServiceProviderId,
        services: servicePrivilegeInfo.map(service => ({
          name: service.serviceInfo.name,
          description: service.serviceInfo.description,
          privileges: service.servicePrivileges.map(privilege => ({
            name: privilege.name,
            description: privilege.description
          }))
        }))
      };
      
      // Also create a flattened array of privilege names for easy access
      const allPrivileges = [];
      servicePrivilegeInfo.forEach(service => {
        service.servicePrivileges.forEach(privilege => {
          allPrivileges.push(privilege.name);
        });
      });
      
      // Store privileges in localStorage for easy access across the app
      localStorage.setItem('document_service_privileges', JSON.stringify(allPrivileges));
      
      // Return the structured data
      return {
        ...documentServicePrivileges,
        privileges: allPrivileges
      };
    })
    .catch((error) => {
      console.error('Error fetching privileges:', error);
      authUtil.clearSession();
      throw error;
    });
}

export default getPrivileges;